import { useDispatch } from 'react-redux';
import { setImageViewer } from '../Redux/store';
import { setTestViewer } from '../Redux/uiSlice';
import { Clock } from 'lucide-react';
import ImageDisplay from './ImageDisplay';
import { formatDateLongNoComma } from '../utils/dateFormat';
import { getTestCD } from '../data/functions';

/** Get purple color classes based on test value (higher = more purple) */
function getTestColorClasses(value) {
  if (value == null) return { text: 'text-muted-foreground', bg: 'bg-muted', border: 'border-border' };
  const v = Number(value);
  if (v === -1) return { text: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/30' };
  if (v < 0.25) return { text: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/30' };
  if (v < 0.45) return { text: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30' };
  if (v < 0.7) return { text: 'text-purple-500', bg: 'bg-purple-500/15', border: 'border-purple-500/40' };
  if (v <= 0.9) return { text: 'text-purple-600', bg: 'bg-purple-600/20', border: 'border-purple-600/50' };
  return { text: 'text-purple-700', bg: 'bg-purple-700/25', border: 'border-purple-700/60' };
}

/** Individual test row component */
export default function TestRow({ test, allTests, uploading = false, onImageClick, calendarData = null }) {
  const dispatch = useDispatch();

  const date = new Date(test.taken_at || test.takenAt || test.startTime);
  const dateStr = formatDateLongNoComma(date);
  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // Show date on top if test is NOT from today
  const isToday = date.toDateString() === new Date().toDateString();
  const showDateOnTop = !isToday;

  const cycleDay = test.cycleDay || getTestCD(test, calendarData);
  const colors = getTestColorClasses(test.value);

  // Get image data - from photo_url (Supabase JSONB), fileUrl (object URL), or imageUrl
  const imageData = test.photo_url || test.fileUrl || test.imageUrl || null;

  const handleImageClick = () => {
    if (uploading) return; // Don't open test viewer for uploads
    // Open test viewer with this test's data
    dispatch(setTestViewer({
      test,
      uploading: false,
    }));
  };

  const handleImageOnlyClick = (e) => {
    if (onImageClick && typeof onImageClick === 'function') {
      e.stopPropagation(); // Prevent row click
      onImageClick(imageData);
    }
  };

  return (
    <li 
      className={`flex items-center gap-3 px-4 py-3 ${colors.bg} ${uploading ? 'opacity-70' : ''} ${!uploading ? 'cursor-pointer' : ''}`}
      onClick={handleImageClick}
    >
      {/* Time and date — time on top when today, date on top when not today */}
      <div className="min-w-0">
        <div className={`text-sm font-semibold ${showDateOnTop ? 'text-muted-foreground' : 'text-foreground'}`}>{showDateOnTop ? dateStr : timeStr}</div>
        <div className={`text-[10px] ${showDateOnTop ? 'text-foreground' : 'text-muted-foreground'}`}>{showDateOnTop ? timeStr : dateStr}</div>
      </div>
      {/* Test image thumbnail */}
      <div 
        className="h-10 flex-1 flex items-center justify-center rounded-md bg-secondary relative overflow-hidden cursor-pointer"
        onClick={handleImageOnlyClick}
      >
        {imageData ? (
          <ImageDisplay 
            imageData={imageData} 
            showIcons={false}
            showLoading={true}
            className="h-full w-full rounded-md"
          />
        ) : (
          <span className="text-xs text-muted-foreground">No image</span>
        )}
        {/* Uploading overlay */}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="flex items-center gap-2 text-xs text-white">
              <Clock className="h-3 w-3" />
              <span>Uploading...</span>
            </div>
          </div>
        )}
      </div>
      {/* Value */}
      <div className="text-right shrink-0">
        <div className={`text-sm font-bold ${colors.text}`}>
          {test.value != null ? Number(test.value).toFixed(1) : '—'}
        </div>
        <div className="text-[10px] text-muted-foreground">CD {cycleDay != null ? cycleDay : '—'}</div>
      </div>
    </li>
  );
}
